import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useClient } from 'components/ClientProvider';
import { InputSize } from 'components/Dropdown/types';
import { useInitializeGoogleTagManager } from 'hooks/useInitializeGoogleTagManager';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { STORE } from '../SkeletonLoader/constants';
import { SkeletonHeader } from '../SkeletonLoader/SkeletonHeader';
import {
  getSelectedStoreOption,
  setSelectedStoreOption,
  setStoreDropdownOptions,
  setDepartmantName
} from './slice';
import { StyledDropdown } from './styled';
import { DropdownOption, StoreSelectProps } from './types';
import { getDropDownOptions } from './utils';

export const StoreSelect = ({
  size,
  errorMessage,
  hasError
}: StoreSelectProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const [storeOptions, setStoreOptions] = useState<DropdownOption[]>([]);
  const selectedStore = useAppSelector(getSelectedStoreOption);
  const client = useClient();
  const stores = client?.customClientValues?.stores;
  const departments = client?.customClientValues?.departments;

  useInitializeGoogleTagManager(
    client?.customClientValues?.stores,
    selectedStore
  );

  useEffect(() => {
    if (stores && departments) {
      const dropdownOptions = getDropDownOptions(stores, departments);
      dispatch(setStoreDropdownOptions(dropdownOptions));
      setStoreOptions(dropdownOptions);
      departments?.forEach(department => {
        if (department.storeId === selectedStore.value) {
          dispatch(setDepartmantName(department.departmentName));
        }
      });
    }
  }, [departments, stores, dispatch, selectedStore]);

  if (storeOptions.length < 1) {
    return <SkeletonHeader type={STORE} />;
  }
  return (
    <StyledDropdown
      dataTestId="ap-payments-store-dropdown"
      inputId="ap-payments-store-dropdown-input"
      hasError={hasError}
      errorMessage={errorMessage}
      label={
        <FormattedMessage
          description="Label for store select dropdown"
          defaultMessage="Store"
        />
      }
      size={size || InputSize.STANDARD}
      isDisabled={storeOptions.length <= 1}
      onChange={(option: DropdownOption) => {
        const department = departments?.find(
          a => a.storeId === option.value
        )?.departmentName;
        if (department) dispatch(setDepartmantName(department));
        dispatch(setSelectedStoreOption(option));
      }}
      menuListStyles={{ maxWidth: '550px', minWidth: '253px' }}
      options={storeOptions}
      value={selectedStore}
    />
  );
};

export default StoreSelect;
